import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { calculateCost } from '../utils/costCalculator';

function AccountsManager() {
  const { isAuthenticated } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);


  const [formData, setFormData] = useState({
    name: '',
    token: '',
    description: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      loadAccounts();
    }
  }, [isAuthenticated]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts.php');
      
      if (response.data.success) {
        setAccounts(response.data.data.accounts || []);
      } else {
        toast.error('Error al cargar las cuentas');
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Error de conexión al cargar las cuentas');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = () => {
    setModalMode('add');
    setSelectedAccount(null);
    setFormData({ name: '', token: '', description: '' });
    setShowModal(true);
  };

  const handleEditAccount = (account) => {
    setModalMode('edit');
    setSelectedAccount(account);
    setFormData({
      name: account.name,
      token: account.token,
      description: account.description || ''
    });
    setShowModal(true);
  };

  const handleViewAccount = (account) => {
    setModalMode('view');
    setSelectedAccount(account);
    setFormData({
      name: account.name,
      token: account.token,
      description: account.description || ''
    });
    setShowModal(true);
  };

  const handleDeleteAccount = (account) => {
    setAccountToDelete(account);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await axios.delete(`/api/accounts_manager.php?id=${accountToDelete.id}`);
      
      if (response.data.success) {
        toast.success('Cuenta eliminada exitosamente');
        loadAccounts();
      } else {
        toast.error('Error al eliminar la cuenta');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Error al eliminar la cuenta');
    } finally {
      setShowDeleteConfirm(false);
      setAccountToDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = modalMode === 'add' ? '/api/accounts_manager.php' : `/api/accounts_manager.php`;
      const method = modalMode === 'add' ? 'post' : 'put';
      
      const dataToSend = modalMode === 'add' ? 
        { name: formData.name, api_token: formData.token } : 
        { id: selectedAccount.id, name: formData.name, api_token: formData.token };
      
      const response = await axios[method](url, dataToSend);
      
      if (response.data.success) {
        toast.success(modalMode === 'add' ? 'Cuenta creada exitosamente' : 'Cuenta actualizada exitosamente');
        setShowModal(false);
        loadAccounts();
      } else {
        toast.error('Error al guardar la cuenta');
      }
    } catch (error) {
      console.error('Error saving account:', error);
      toast.error('Error al guardar la cuenta');
    }
  };



  const filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUsagePercentage = (used, total) => {
    if (!total) return 0;
    return Math.round((used / total) * 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    if (percentage >= 50) return 'bg-blue-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Cuentas</h1>
          <p className="text-gray-600 mt-1">Administra tus cuentas de SendGrid</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadAccounts}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>Actualizar</span>
          </button>
          <button
            onClick={handleAddAccount}
            className="btn btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Nueva Cuenta</span>
          </button>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Buscar cuentas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Tabla de cuentas */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Cuentas ({filteredAccounts.length})
          </h3>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Cuenta</th>
                  <th className="text-right">Límite</th>
                  <th className="text-right">Usado</th>
                  <th className="hidden lg:table-cell text-right">Restante</th>
                  <th className="text-center">Uso %</th>
                  <th className="hidden sm:table-cell text-right">Costo</th>
                  <th className="hidden md:table-cell text-center">Estado</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => {
                  const usagePercentage = getUsagePercentage(account.used, account.total);
                  return (
                    <tr key={account.id}>
                      <td className="font-medium">{account.name}</td>
                      <td className="text-right">{parseInt(account.total).toLocaleString()}</td>
                      <td className="text-right">{parseInt(account.used).toLocaleString()}</td>
                      <td className="hidden lg:table-cell text-right">{parseInt(account.remain).toLocaleString()}</td>
                      <td className="text-center">
                        <div className="flex items-center justify-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className={`h-2 rounded-full ${getUsageColor(usagePercentage)}`}
                              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">{usagePercentage}%</span>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell text-right">${calculateCost(parseInt(account.used) || 0).toFixed(2)}</td>
                      <td className="hidden md:table-cell text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          usagePercentage >= 90 ? 'bg-red-100 text-red-800' :
                          usagePercentage >= 75 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {usagePercentage >= 90 ? 'Crítico' :
                           usagePercentage >= 75 ? 'Advertencia' : 'Normal'}
                        </span>
                      </td>
                      <td>
                        <div className="flex space-x-2 justify-center">
                          <button
                            onClick={() => handleViewAccount(account)}
                            className="btn btn-secondary btn-sm"
                            title="Ver detalles"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditAccount(account)}
                            className="btn btn-primary btn-sm"
                            title="Editar"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAccount(account)}
                            className="btn btn-danger btn-sm"
                            title="Eliminar"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar/ver cuenta */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {modalMode === 'add' ? 'Nueva Cuenta' :
                 modalMode === 'edit' ? 'Editar Cuenta' : 'Detalles de Cuenta'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      disabled={modalMode === 'view'}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Token SendGrid</label>
                    <textarea
                      value={formData.token}
                      onChange={(e) => setFormData({...formData, token: e.target.value})}
                      disabled={modalMode === 'view'}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Descripción (opcional)</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      disabled={modalMode === 'view'}
                      rows={2}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary"
                  >
                    {modalMode === 'view' ? 'Cerrar' : 'Cancelar'}
                  </button>
                  {modalMode !== 'view' && (
                    <button type="submit" className="btn btn-primary">
                      {modalMode === 'add' ? 'Crear' : 'Actualizar'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <TrashIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Eliminar Cuenta</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  ¿Estás seguro de que quieres eliminar la cuenta "{accountToDelete?.name}"?
                  Esta acción no se puede deshacer.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="btn btn-danger"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default AccountsManager;